import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '../../../lib/api/services';

import { UserCreateInput, UserListResponse, UserResponse, UserResiliationResponse, UserSelectInput, UserUpdateInput } from '@repo/types';
import { useQueryWithConfig } from '../useQueryWithConfig';

// Hooks for fetching users
export const useUsers = (filters: UserSelectInput) => {
  const { page = 1, limit = 10, sortBy, ...filterParams } = filters;
  const keys = ["users", "list", page, limit, sortBy, JSON.stringify(filterParams)].filter(Boolean);
  
  return useQueryWithConfig<UserListResponse>(
    keys as string[],
    () => services.user.getUsers(filters)
  );
};

export const useUser = (id: number) => {
  return useQueryWithConfig<UserResponse>(
    ["users", "detail", id],
    () => services.user.getUserById(id),
    {
      enabled: !!id,
    }
  );
};

export const useUserProfile = () => {
  return useQueryWithConfig<UserResponse>(
    ["users", "profile"],
    () => services.user.getUserProfile()
  );
};

// Hooks for user mutations
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UserResponse, Error, UserCreateInput>({
    mutationFn: (data) => services.user.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UserResponse, Error, { id: number; data: UserUpdateInput }>({
    mutationFn: ({ id, data }) => services.user.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UserResponse, Error, number>({
    mutationFn: (id) => services.user.deleteUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
};

export const useActivateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UserResponse, Error, number>({
    mutationFn: (id) => services.user.activateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
};

export const useDeactivateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation<UserResponse, Error, number>({
    mutationFn: (id) => services.user.deactivateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
};

export const useResiliateUser = () => {
  const queryClient = useQueryClient();

  return useMutation<UserResiliationResponse, Error, number>({
    mutationFn: (id) => services.user.resiliateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
};

export const useDeleteMyAccount = () => {
  return useMutation<UserResponse, Error, void>({
    mutationFn: () => services.user.deleteMyAccount(),
  });
};

export const useResiliateMyAccount = () => {
  return useMutation<UserResiliationResponse, Error, void>({
    mutationFn: () => services.user.resiliateMyAccount(),
  });
};

export const useChangeUserPassword = () => {
  return useMutation<void, Error, { userId: number; newPassword: string; passwordConfirm: string }>({
    mutationFn: ({ userId, newPassword, passwordConfirm }) =>
      services.user.adminChangePassword(userId, newPassword, passwordConfirm),
  });
};

export const useChangeSelfPassword = () => {
  return useMutation<void, Error, { currentPassword: string; newPassword: string; passwordConfirm: string }>({
    mutationFn: ({ currentPassword, newPassword, passwordConfirm }) => 
      services.user.changeSelfPassword(currentPassword, newPassword, passwordConfirm),
  });
};
export const useUploadUserAvatar = () => {
  const queryClient = useQueryClient();
  
  return useMutation<string, Error, { userId: number; file: File }>({
    mutationFn: ({ userId, file }) => services.user.uploadAvatar(userId, file),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", userId] });
      queryClient.invalidateQueries({ queryKey: ["users", "profile"] });
    },
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  return useMutation<string, Error, { profilId: number; userId: number }>({
    mutationFn: ({ profilId, userId }) => services.user.updateUserProfile(userId, profilId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "profile"] });
    },
  });
}

export const useUpdateUserProfileById = () => {
  const queryClient = useQueryClient();
  return useMutation<string, Error, { userId: number; profilId: number; }>({
    mutationFn: ({ userId, profilId }) => services.user.updateUserProfileById(userId, profilId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["users", "detail", userId] });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}
